import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemFiltersModal} from '@Modals/poster-editor-item-filters-modal';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {openPosterEditorItemAdjustmentsModal} from '@Modals/poster-editor-item-adjustments-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {useEditorPrefetchMediaMobileModules} from '@Hooks/poster-editor/useEditorPrefetchMediaMobileModules';
import {StickerItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from '@Components/poster-editor/components/poster-editor-bottom-bar/components/qr-bottom-bar/qr-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function StickerBottomBar(): ReactElement<any> | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  useEditorPrefetchMediaMobileModules();

  if (!activeItem?.isSticker()) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: StickerItemBottomBarOptions): void => {
    switch (itemId) {
      case StickerItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case StickerItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case StickerItemBottomBarOptions.FILTERS:
        openPosterEditorItemFiltersModal();
        break;
      case StickerItemBottomBarOptions.EFFECTS:
        openPosterEditorItemEffectsModal();
        break;
      case StickerItemBottomBarOptions.ADJUSTMENTS:
        openPosterEditorItemAdjustmentsModal();
        break;
      case StickerItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case StickerItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case StickerItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case StickerItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case StickerItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case StickerItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case StickerItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case StickerItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem?.uid);
        break;
      default:
    }
  };

  return (
    <BottomBar
      id="poster-editing-sticker-bottom-bar"
      items={[
        {
          id: StickerItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: StickerItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: StickerItemBottomBarOptions.FILTERS,
          icon: 'icon-filters',
          text: window.i18next.t('pmwjs_filters'),
        },
        {
          id: StickerItemBottomBarOptions.EFFECTS,
          icon: 'icon-effects',
          text: window.i18next.t('pmwjs_effects'),
        },
        {
          id: StickerItemBottomBarOptions.ADJUSTMENTS,
          icon: 'icon-filter-1',
          text: window.i18next.t('pmwjs_adjustments'),
        },
        {
          id: StickerItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: StickerItemBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: StickerItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: StickerItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: StickerItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: StickerItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: StickerItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: StickerItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
      onClick={onClickHandler}
    />
  );
}
